import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import dayjs from 'dayjs';
import { AlertCircle, ListChecks, Loader2 } from 'lucide-react';
import { authenticationAtom } from '../State';
import { userAPI } from '../UserAPI';
import { useCustomerTheme } from '../Theme';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { ChecklistRunner } from './ChecklistRunner';

// History helpers — mirror tri-portal `ProcessPanel` / the app's
// `processInstance` util so the timeline renders the same way regardless of
// which engine path serialized the payload.

/** Tri-server may serialize history under several keys depending on engine path. */
function normalizeProcessHistory(detail: any): any[] {
    if (!detail) return [];
    const unwrap = (h: unknown): any[] => {
        if (h == null) return [];
        if (Array.isArray(h)) return h;
        if (typeof h === 'object' && Array.isArray((h as any).entries)) return (h as any).entries;
        return [];
    };
    const fromTop = unwrap(detail.processHistory);
    if (fromTop.length > 0) return fromTop;
    const fromEngine = unwrap(detail.engine?.history);
    if (fromEngine.length > 0) return fromEngine;
    if (Array.isArray(detail.processInstanceHistory)) return detail.processInstanceHistory;
    return [];
}

/** Best-effort label for who performed a history step (server keys vary). */
function formatProcessHistoryActor(row: any): string {
    if (row == null) return '';
    const direct =
        row.userEmail ?? row.performedByEmail ?? row.assigneeEmail ?? row.actorEmail ?? row.completedByEmail;
    if (typeof direct === 'string' && direct.trim() !== '') return direct.trim();
    const u = row.user ?? row.performedBy ?? row.actor ?? row.assignee ?? row.completedBy ?? row.completedByUser;
    if (u == null || u === '') return '';
    if (typeof u === 'string') return u.trim();
    if (typeof u === 'object') {
        const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
        if (name) return name;
        if (u.email != null && String(u.email).trim() !== '') return String(u.email).trim();
        if (u.name != null && String(u.name).trim() !== '') return String(u.name).trim();
        if (u.displayName != null && String(u.displayName).trim() !== '') return String(u.displayName).trim();
    }
    return '';
}

function formatTimestamp(v: unknown): string {
    if (v == null) return '–';
    try {
        const d = dayjs(v as any);
        return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : String(v);
    } catch {
        return String(v);
    }
}

/** Pull the work-item id from a history row — server keys vary. */
function readRowWorkItemId(row: any): string | null {
    const id =
        row?.bpmWorkItemId ?? row?.workItemId ?? row?.taskId ??
        row?.workItem?._id ?? row?.workItem?.id ?? row?.task?._id ?? row?.task?.id;
    return id != null && String(id) !== '' ? String(id) : null;
}

function buildChecklistByWorkItemId(instances: any[]): Record<string, any> {
    const map: Record<string, any> = {};
    for (const inst of instances) {
        const wid = inst?.bpmWorkItemId ?? inst?.workItemId ?? inst?.taskId;
        if (typeof wid === 'string' && wid.length > 0) map[wid] = inst;
    }
    return map;
}

function extractSynopsis(detail: any): string | null {
    const raw = detail?.instance?.instanceSynopsis ?? detail?.instanceSynopsis ?? null;
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
}

type Phase =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; rows: any[]; synopsis: string | null; checklistByWorkItemId: Record<string, any> }
    | { kind: 'error'; message: string };

interface Properties {
    /** When non-null the modal is open and loads this process instance's history. */
    processInstanceId: string | null;
    onDismiss: () => void;
}

export const ProcessHistoryModal = ({ processInstanceId, onDismiss }: Properties) => {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { theme } = useCustomerTheme();
    // Kundenfarbe der Schrittnummer — Laufzeitwert, deshalb Inline-Style.
    // Ohne Kundenfarbe trägt die Plakette den `primary`-Token.
    const accent: string | undefined = theme.primaryDark || theme.primary || undefined;
    const customer = authentication?.customer;
    const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
    const [viewingChecklist, setViewingChecklist] = useState<any | null>(null);
    const seqRef = useRef(0);

    useEffect(() => {
        if (!processInstanceId) {
            setPhase({ kind: 'idle' });
            return;
        }
        if (!customer) {
            setPhase({ kind: 'error', message: t('processPanel.processRunEmpty') });
            return;
        }
        const seq = ++seqRef.current;
        setPhase({ kind: 'loading' });
        (async () => {
            try {
                const [detail, checklistInstances] = await Promise.all([
                    userAPI.getProcessInstance(customer, processInstanceId),
                    userAPI.getProcessInstanceChecklistInstances(customer, processInstanceId),
                ]);
                if (seq !== seqRef.current) return;
                if (!detail) {
                    setPhase({ kind: 'error', message: t('processPanel.processRunEmpty') });
                    return;
                }
                setPhase({
                    kind: 'ready',
                    rows: normalizeProcessHistory(detail),
                    synopsis: extractSynopsis(detail),
                    checklistByWorkItemId: buildChecklistByWorkItemId(checklistInstances ?? []),
                });
            } catch (e) {
                if (seq !== seqRef.current) return;
                console.error('[ProcessHistory] load failed', e);
                setPhase({ kind: 'error', message: t('processPanel.processRunEmpty') });
            }
        })();
    }, [processInstanceId, customer, t]);

    const sortedRows = phase.kind === 'ready'
        ? [...phase.rows].sort((a, b) => (Number(a?.seq) || 0) - (Number(b?.seq) || 0))
        : [];

    return (
        <>
            <Dialog
                open={!!processInstanceId}
                onOpenChange={next => {
                    if (!next) onDismiss();
                }}
            >
                <DialogContent
                    data-testid='chat-history-modal'
                    className='w-[calc(100vw-2rem)] max-w-[720px] sm:min-w-[520px]'
                >
                    <DialogHeader>
                        <DialogTitle>
                            {t('chatBotPage.processHistoryTitle', { defaultValue: 'Prozesshistorie' })}
                        </DialogTitle>
                        {phase.kind === 'ready' && phase.synopsis && (
                            <DialogDescription>{phase.synopsis}</DialogDescription>
                        )}
                    </DialogHeader>
                    <div className='max-h-[60vh] overflow-y-auto p-5'>
                        {phase.kind === 'loading' ? (
                            <div className='flex items-center gap-5'>
                                <Loader2 className='h-4 w-4 animate-spin text-primary' aria-hidden />
                                <span className='text-sm text-muted-foreground'>
                                    {t('global.loading', { defaultValue: 'Loading…' })}
                                </span>
                            </div>
                        ) : phase.kind === 'error' ? (
                            <div className='flex items-center gap-5'>
                                <AlertCircle className='h-4 w-4 shrink-0 text-destructive' aria-hidden />
                                <span className='text-sm text-muted-foreground'>{phase.message}</span>
                            </div>
                        ) : phase.kind === 'ready' ? (
                            sortedRows.length === 0 ? (
                                <span className='text-sm text-muted-foreground'>
                                    {t('processPanel.processRunEmpty')}
                                </span>
                            ) : (
                                sortedRows.map((row: any, i: number) => {
                                    const seq = row?.seq ?? i + 1;
                                    const ev = row?.eventType ?? row?.type ?? row?.event;
                                    const eventLabel = ev != null && ev !== ''
                                        ? t(`processPanel.processEventType.${String(ev)}`, { defaultValue: String(ev) })
                                        : '–';
                                    const when = formatTimestamp(row?.at ?? row?.timestamp ?? row?.completedAt ?? row?.createdAt);
                                    const nodeName = row?.nodeName ?? row?.nodeId ?? '–';
                                    const nt = row?.nodeType;
                                    const nodeType = nt != null && nt !== ''
                                        ? t(`processPanel.processHistoryNodeTypeKind.${String(nt)}`, { defaultValue: String(nt) })
                                        : '–';
                                    const actor = formatProcessHistoryActor(row) || '–';
                                    const wid = readRowWorkItemId(row);
                                    const checklistInstance = wid && String(ev).toUpperCase() === 'TASK_COMPLETED'
                                        ? phase.checklistByWorkItemId[wid] ?? null
                                        : null;
                                    return (
                                        <div
                                            key={`hist-${i}`}
                                            data-testid='chat-history-row'
                                            className={cn(
                                                'flex items-start gap-2.5 py-2.5',
                                                i > 0 && 'border-t',
                                            )}
                                        >
                                            <div
                                                className={cn(
                                                    'flex h-[26px] min-w-[26px] shrink-0 items-center justify-center rounded-full px-1.5 text-xs font-semibold text-primary-foreground',
                                                    !accent && 'bg-primary',
                                                )}
                                                style={accent ? { backgroundColor: accent } : undefined}
                                            >
                                                {String(seq)}
                                            </div>
                                            <div className='min-w-0 flex-1'>
                                                <div className='flex items-center gap-5'>
                                                    <span className='min-w-0 flex-1 text-sm font-semibold'>
                                                        {eventLabel}
                                                    </span>
                                                    {checklistInstance && (
                                                        <Button
                                                            type='button'
                                                            variant='ghost'
                                                            size='icon'
                                                            className='h-8 w-8'
                                                            data-testid='chat-history-open-checklist'
                                                            title={t('processPanel.openChecklist', { defaultValue: 'Open checklist' })}
                                                            aria-label={t('processPanel.openChecklist', { defaultValue: 'Open checklist' })}
                                                            onClick={() => setViewingChecklist(checklistInstance)}
                                                        >
                                                            <ListChecks className='h-4 w-4' aria-hidden />
                                                        </Button>
                                                    )}
                                                </div>
                                                <div className='mt-0.5 text-xs text-muted-foreground'>{when}</div>
                                                <div className='mt-1.5 flex flex-col gap-0.5'>
                                                    <HistoryMeta label={t('processPanel.processHistoryNode')} value={String(nodeName)} />
                                                    <HistoryMeta label={t('processPanel.processHistoryNodeType')} value={nodeType} />
                                                    <HistoryMeta label={t('processPanel.processHistoryPerformedBy')} value={actor} />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )
                        ) : null}
                    </div>
                    <DialogFooter className='gap-2 pt-4 sm:gap-0'>
                        <Button
                            type='button'
                            variant='outline'
                            data-testid='chat-history-close'
                            onClick={onDismiss}
                        >
                            {t('global.close', { defaultValue: 'Close' })}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={!!viewingChecklist}
                onOpenChange={next => {
                    if (!next) setViewingChecklist(null);
                }}
            >
                <DialogContent
                    data-testid='chat-history-checklist-modal'
                    className='w-[calc(100vw-2rem)] max-w-[720px] sm:min-w-[520px]'
                >
                    <DialogHeader>
                        <DialogTitle>
                            {viewingChecklist?.name ?? t('processPanel.openChecklist', { defaultValue: 'Open checklist' })}
                        </DialogTitle>
                    </DialogHeader>
                    <div className='p-5'>
                        {viewingChecklist && <ChecklistRunner instance={viewingChecklist} readOnly />}
                    </div>
                    <DialogFooter className='gap-2 pt-4 sm:gap-0'>
                        <Button
                            type='button'
                            variant='outline'
                            data-testid='chat-history-checklist-close'
                            onClick={() => setViewingChecklist(null)}
                        >
                            {t('global.close', { defaultValue: 'Close' })}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

function HistoryMeta({ label, value }: { label: string; value: string }) {
    return (
        <div className='flex items-start gap-2'>
            <span className='w-[110px] shrink-0 text-xs text-muted-foreground'>{label}</span>
            <span className='flex-1 text-xs'>{value}</span>
        </div>
    );
}
